import { IContentSafetyScanResult } from "../types/safety";

/**
 * Banned keywords and phrases for lexical filename, metadata, and message text moderation.
 */
export const PORNOGRAPHY_TERMS: readonly string[] = [
  "porn",
  "pornography",
  "xxx",
  "nsfw",
  "nude",
  "nudity",
  "erotic",
  "hentai",
  "sex",
  "boobs",
  "vagina",
  "penis",
  "blowjob",
  "playboy",
  "fetish",
  "hardcore",
  "camgirl",
  "lewd",
  "stripper",
];

/**
 * Banned violence keywords and phrases for lexical text moderation.
 */
export const VIOLENCE_TERMS: readonly string[] = [
  "gore",
  "beheading",
  "massacre",
  "execution",
  "gunshot",
  "murder",
  "bloodshed",
  "decapitation",
  "mutilation",
  "torture",
  "suicide",
  "lynch",
  "carnage",
  "terrorist",
  "combat",
  "corpse",
];

/**
 * Banned discriminatory and hate-speech keywords for lexical text moderation.
 */
export const DISCRIMINATORY_TERMS: readonly string[] = [
  "nigger",
  "nigga",
  "chink",
  "kike",
  "spic",
  "gook",
  "faggot",
  "fag",
  "retard",
  "tranny",
  "swastika",
  "white supremacist",
  "neo nazi",
  "neo-nazi",
  "nazi",
  "aryan",
  "hitler",
  "hate crime",
  "genocide",
  "apartheid",
  "kkk",
  "klan",
];

/**
 * Evaluates arbitrary text (e.g. filename, metadata, message body) against community safety rules.
 *
 * @param {string} text - The text to inspect.
 * @returns {IContentSafetyScanResult} Result indicating whether the text is safe or violates policy.
 */
export function scanTextForViolations(text: string): IContentSafetyScanResult {
  if (!text || text.trim() === "") {
    return { safe: true };
  }

  const clean = text
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[^a-z0-9\s]/g, " ");

  const words = clean.split(/\s+/).filter(Boolean);
  const paddedClean = ` ${words.join(" ")} `;

  const matchesCategory = (terms: readonly string[]): boolean => {
    return terms.some((term) => {
      const normalizedTerm = term.toLowerCase().replace(/[^a-z0-9\s]/g, " ").trim();
      return words.includes(normalizedTerm) || paddedClean.includes(` ${normalizedTerm} `);
    });
  };

  if (matchesCategory(DISCRIMINATORY_TERMS)) {
    return {
      safe: false,
      category: "discriminatory",
      reason: "Blocked: Discriminatory, hateful, or racial content violates community standards and cannot be stored.",
    };
  }

  if (matchesCategory(PORNOGRAPHY_TERMS)) {
    return {
      safe: false,
      category: "pornography",
      reason: "Blocked: Explicit adult content or pornography is strictly prohibited on Memories.",
    };
  }

  if (matchesCategory(VIOLENCE_TERMS)) {
    return {
      safe: false,
      category: "violence",
      reason: "Blocked: Content depicting violence, weapons, or graphic harm is strictly prohibited on Memories.",
    };
  }

  return { safe: true };
}

/**
 * Checks for dangerous executable file signatures in a binary byte array or Buffer.
 *
 * @param {Uint8Array} bytes - Raw file byte array or Buffer.
 * @returns {boolean} True if the file has dangerous executable signatures.
 */
export function isExecutableOrMaliciousBytes(bytes: Uint8Array): boolean {
  if (!bytes || bytes.length < 4) {
    return false;
  }

  // Windows PE/EXE/DLL: 'MZ' (0x4D, 0x5A)
  if (bytes[0] === 0x4d && bytes[1] === 0x5a) {
    return true;
  }

  // Linux ELF: 0x7F, 'E', 'L', 'F' (0x7F, 0x45, 0x4C, 0x46)
  if (bytes[0] === 0x7f && bytes[1] === 0x45 && bytes[2] === 0x4c && bytes[3] === 0x46) {
    return true;
  }

  // Java Bytecode class: 0xCA, 0xFE, 0xBA, 0xBE
  if (bytes[0] === 0xca && bytes[1] === 0xfe && bytes[2] === 0xba && bytes[3] === 0xbe) {
    return true;
  }

  // Mach-O binary (macOS 32/64 bit): 0xFE, 0xED, 0xFA, 0xCE or 0xCF
  if (
    (bytes[0] === 0xfe && bytes[1] === 0xed && bytes[2] === 0xfa && (bytes[3] === 0xce || bytes[3] === 0xcf)) ||
    (bytes[0] === 0xcf && bytes[1] === 0xfa && bytes[2] === 0xed && bytes[3] === 0xfe)
  ) {
    return true;
  }

  // Shell script with shebang: '#!' (0x23, 0x21)
  if (bytes[0] === 0x23 && bytes[1] === 0x21) {
    return true;
  }

  return false;
}

/**
 * Verifies that the binary magic bytes match the claimed content type.
 *
 * @param {Uint8Array} bytes - Raw file byte array or Buffer.
 * @param {string} declaredMimeType - Declared MIME type.
 * @returns {boolean} True if the magic bytes match the declared MIME type.
 */
export function verifyMagicBytes(bytes: Uint8Array, declaredMimeType: string): boolean {
  if (!bytes || bytes.length < 4) {
    return false;
  }

  const mime = (declaredMimeType || "").toLowerCase().trim();

  // JPEG / JPG: 0xFF, 0xD8, 0xFF
  if (mime === "image/jpeg" || mime === "image/jpg") {
    return bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  }

  // PNG: 0x89, 'P', 'N', 'G', 0x0D, 0x0A, 0x1A, 0x0A
  if (mime === "image/png") {
    return (
      bytes.length >= 8 &&
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47 &&
      bytes[4] === 0x0d &&
      bytes[5] === 0x0a &&
      bytes[6] === 0x1a &&
      bytes[7] === 0x0a
    );
  }

  // GIF: 'GIF8' (0x47, 0x49, 0x46, 0x38)
  if (mime === "image/gif") {
    return bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38;
  }

  // WebP: 'RIFF' .... 'WEBP'
  if (mime === "image/webp") {
    if (bytes.length < 12) return false;
    const isRiff = bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46;
    const isWebp = bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50;
    return isRiff && isWebp;
  }

  // MP4 / QuickTime: offset 4 contains 'ftyp' (0x66, 0x74, 0x79, 0x70)
  if (mime === "video/mp4" || mime === "video/quicktime") {
    if (bytes.length < 8) return false;
    return bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70;
  }

  // MP3: 'ID3' (0x49, 0x44, 0x33) or MPEG sync frame (0xFF, 0xFB/F3/F2)
  if (mime === "audio/mpeg" || mime === "audio/mp3") {
    const isId3 = bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33;
    const isMpegSync = bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0;
    return isId3 || isMpegSync;
  }

  // WAV: 'RIFF' .... 'WAVE'
  if (mime === "audio/wav" || mime === "audio/x-wav") {
    if (bytes.length < 12) return false;
    const isRiff = bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46;
    const isWave = bytes[8] === 0x57 && bytes[9] === 0x41 && bytes[10] === 0x56 && bytes[11] === 0x45;
    return isRiff && isWave;
  }

  // Fallback: If generic media type starts with audio/, video/, or image/, allow if not executable
  if (mime.startsWith("audio/") || mime.startsWith("video/") || mime.startsWith("image/")) {
    return !isExecutableOrMaliciousBytes(bytes);
  }

  // PDF: '%PDF-' (0x25, 0x50, 0x44, 0x46)
  if (mime === "application/pdf") {
    return bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
  }

  // ZIP / Compressed Archives: strictly not accepted
  if (
    mime === "application/zip" ||
    mime === "application/x-zip-compressed" ||
    mime === "application/x-tar" ||
    mime === "application/gzip" ||
    mime === "application/x-7z-compressed" ||
    mime === "application/vnd.rar" ||
    mime.includes("zip") ||
    mime.includes("tar") ||
    mime.includes("compressed")
  ) {
    return false;
  }

  // Documents: Office, text, RTF, CSV, Markdown, OpenDocument
  if (
    mime.startsWith("text/") ||
    mime.includes("word") ||
    mime.includes("officedocument") ||
    mime.includes("document") ||
    mime.includes("sheet") ||
    mime.includes("excel") ||
    mime.includes("presentation") ||
    mime.includes("powerpoint") ||
    mime === "application/rtf" ||
    mime === "application/msword" ||
    mime.includes("opendocument")
  ) {
    return !isExecutableOrMaliciousBytes(bytes);
  }

  return false;
}
